import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { acknowledgeError, acknowledgeAllErrors, clearAllErrors, toggleErrorConsole } from '../../store/errorSlice';
import { FiX, FiCheck, FiTrash2 } from 'react-icons/fi';

const ErrorConsole: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { errors, showErrorConsole } = useSelector((state: RootState) => state.error);

  if (!showErrorConsole) return null;

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error': return 'border-red-500 bg-red-50';
      case 'warning': return 'border-yellow-500 bg-yellow-50';
      case 'info': return 'border-blue-500 bg-blue-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  return (
    <div className="fixed top-16 right-4 w-96 max-h-96 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-800">Error Console</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => dispatch(acknowledgeAllErrors())}
            className="p-1 text-green-600 hover:bg-green-50 rounded"
            title="Mark all as read"
          >
            <FiCheck size={16} />
          </button>
          <button
            onClick={() => dispatch(clearAllErrors())}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
            title="Clear all errors"
          >
            <FiTrash2 size={16} />
          </button>
          <button
            onClick={() => dispatch(toggleErrorConsole())}
            className="p-1 text-gray-600 hover:bg-gray-50 rounded"
            title="Close"
          >
            <FiX size={16} />
          </button>
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {errors.length === 0 ? (
          <div className="p-4 text-center text-gray-500">No errors</div>
        ) : (
          errors.map((error) => (
            <div
              key={error.id}
              className={`p-3 border-l-4 ${getSeverityColor(error.severity)} ${!error.acknowledged ? 'bg-opacity-75' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-medium text-sm">{error.title}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {new Date(error.timestamp).toLocaleTimeString()}
                  </div>
                  <div className="text-sm mt-1">{error.message}</div>
                  {error.details && (
                    <details className="mt-2">
                      <summary className="text-xs cursor-pointer text-gray-600">Details</summary>
                      <pre className="text-xs mt-1 bg-gray-100 p-2 rounded overflow-x-auto">
                        {JSON.stringify(error.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
                {!error.acknowledged && (
                  <button
                    onClick={() => dispatch(acknowledgeError(error.id))}
                    className="ml-2 p-1 text-green-600 hover:bg-green-50 rounded"
                    title="Mark as read"
                  >
                    <FiCheck size={14} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ErrorConsole;
